import imageCompression from 'browser-image-compression';

/**
 * Image compression options for different use cases
 */
export interface CompressionOptions {
  maxSizeMB?: number;           // Maximum file size in MB (default: 1)
  maxWidthOrHeight?: number;    // Maximum dimension in pixels (default: 1200)
  quality?: number;             // JPEG quality 0-1 (default: 0.8)
  useWebWorker?: boolean;       // Use web worker for better performance (default: true)
}

/**
 * Default compression settings optimized for profile/avatar images
 * Reduces 5-10MB mobile photos to ~200-500KB while maintaining good quality
 */
const DEFAULT_OPTIONS: CompressionOptions = {
  maxSizeMB: 1,
  maxWidthOrHeight: 1200,
  quality: 0.8,
  useWebWorker: true
};

/**
 * Preset for avatar/profile images - smaller size, square-friendly
 */
export const AVATAR_PRESET: CompressionOptions = {
  maxSizeMB: 0.25,
  maxWidthOrHeight: 700,
  quality: 0.8,
  useWebWorker: true
};

/**
 * Preset for gallery/product images - higher quality, larger size
 */
export const GALLERY_PRESET: CompressionOptions = {
  maxSizeMB: 0.8,
  maxWidthOrHeight: 1400,
  quality: 0.82,
  useWebWorker: true
};

/**
 * Compress an image file to reduce size before upload
 * Handles large mobile photos (5-10MB) and reduces them to web-friendly sizes
 * 
 * @param file - The original image file from input
 * @param options - Compression options (optional, uses defaults if not provided)
 * @returns Compressed file ready for upload
 * 
 * @example
 * // Basic usage with defaults
 * const compressed = await compressImage(file);
 * 
 * // With avatar preset
 * const avatar = await compressImage(file, AVATAR_PRESET);
 * 
 * // With custom options
 * const custom = await compressImage(file, { maxSizeMB: 0.3, maxWidthOrHeight: 600 });
 */
export async function compressImage(
  file: File, 
  options: CompressionOptions = {}
): Promise<File> {
  // Merge provided options with defaults
  const compressionOptions = {
    ...DEFAULT_OPTIONS,
    ...options
  };

  // Skip compression for already small files (under 500KB)
  if (file.size < 500 * 1024) {
    console.log(`Image already small (${formatFileSize(file.size)}), skipping compression`);
    return file;
  }

  const originalSize = file.size;
  console.log(`Compressing image: ${file.name} (${formatFileSize(originalSize)})`);

  try {
    const compressedFile = await imageCompression(file, {
      maxSizeMB: compressionOptions.maxSizeMB!,
      maxWidthOrHeight: compressionOptions.maxWidthOrHeight!,
      initialQuality: compressionOptions.quality!,
      useWebWorker: compressionOptions.useWebWorker!,
      fileType: file.type as 'image/jpeg' | 'image/png' | 'image/webp',
    });

    const compressedSize = compressedFile.size;
    const reduction = ((originalSize - compressedSize) / originalSize * 100).toFixed(1);
    
    console.log(
      `Compression complete: ${formatFileSize(originalSize)} → ${formatFileSize(compressedSize)} ` +
      `(${reduction}% reduction)`
    );

    return compressedFile;
  } catch (error) {
    console.error('Error compressing image:', error);
    // Return original file if compression fails - don't block the upload
    console.warn('Returning original file due to compression error');
    return file;
  }
}

/**
 * Compress image and generate a preview URL
 * Useful for showing preview while compression happens
 * 
 * @param file - The original image file
 * @param options - Compression options
 * @returns Object with compressed file and preview data URL
 */
export async function compressImageWithPreview(
  file: File,
  options: CompressionOptions = {}
): Promise<{ file: File; previewUrl: string }> {
  // Generate preview from original file immediately
  const previewUrl = await generatePreviewUrl(file);
  
  // Compress the file
  const compressedFile = await compressImage(file, options);
  
  return {
    file: compressedFile,
    previewUrl
  };
}

/**
 * Generate a data URL preview from a file
 * @param file - Image file to preview
 * @returns Data URL string for img src
 */
export function generatePreviewUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Check if a file is an image
 * @param file - File to check
 * @returns true if file is an image type
 */
export function isImageFile(file: File): boolean {
  return file.type.startsWith('image/');
}

/**
 * Format file size for display
 * @param bytes - File size in bytes
 * @returns Formatted string (e.g., "2.5 MB")
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

/**
 * Validate image file before processing
 * @param file - File to validate
 * @param maxSizeMB - Maximum allowed size in MB (default: 20)
 * @returns Object with isValid flag and error message if invalid
 */
export function validateImageFile(
  file: File, 
  maxSizeMB: number = 20
): { isValid: boolean; error?: string } {
  // Check if it's an image
  if (!isImageFile(file)) {
    return { 
      isValid: false, 
      error: 'Please select an image file (JPEG, PNG, GIF, or WebP)' 
    };
  }

  // Check file size (allow up to maxSizeMB before compression)
  const maxBytes = maxSizeMB * 1024 * 1024;
  if (file.size > maxBytes) {
    return { 
      isValid: false, 
      error: `Image is too large (${formatFileSize(file.size)}). Maximum size is ${maxSizeMB}MB.` 
    };
  }

  return { isValid: true };
}